// === INPACT:SLUITSTRAAT:BOILERPLATE:ROUTER:v2 ===

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useProductSelection, useConfig, shouldShowProductScreen, LoadingScreen } from '@sluitstraat/core';

// Eagerly loaded: lightweight pages that are part of the initial navigation
import NotFoundPage from './pages/NotFoundPage';
import ProductSelectionScreen from './pages/screens/ProductSelectionScreen';

// Lazy loaded: settings pages (admin-only, not part of main workflow)
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const CustomerInformationPage = React.lazy(() => import('./pages/CustomerInformationPage'));
const ModuleSelectionPage = React.lazy(() => import('./pages/ModuleSelectionPage'));
const SluitstraatInformationPage = React.lazy(() => import('./pages/SluitstraatInformationPage'));
const WorkflowConfigurationPage = React.lazy(() => import('./pages/WorkflowConfigurationPage'));
const TextsPage = React.lazy(() => import('./pages/TextsPage'));

// Lazy loaded: workflow screens (loaded on-demand as user progresses)
const ClientInformationScreen = React.lazy(() => import('./pages/screens/ClientInformationScreen'));
const WoonhuisCvbScreen = React.lazy(() => import('./pages/screens/WoonhuisCvbScreen'));
const InboedelCvbScreen = React.lazy(() => import('./pages/screens/InboedelCvbScreen'));
const AansprakelijkheidCvbScreen = React.lazy(() => import('./pages/screens/AansprakelijkheidCvbScreen'));
const PaymentInformationScreen = React.lazy(() => import('./pages/screens/PaymentInformationScreen'));
const WorkflowSummaryScreen = React.lazy(() => import('./pages/screens/WorkflowSummaryScreen'));
const AcceptanceQuestionsScreen = React.lazy(() => import('./pages/screens/AcceptanceQuestionsScreen'));
const ConfirmationScreen = React.lazy(() => import('./pages/screens/ConfirmationScreen'));
const EndPageScreen = React.lazy(() => import('./pages/screens/EndPageScreen'));

/**
 * ProductRoute - Guards product screens behind product selection.
 * Redirects to product-selection if the required product has not been selected.
 * Has its own Suspense boundary so the loading screen shows immediately
 * instead of being deferred by React 18's startTransition.
 */
const ProductRoute: React.FC<{ productId: string; logo?: string; children: React.ReactNode }> = ({ productId, logo, children }) => {
  const { selectedProducts } = useProductSelection();

  if (!shouldShowProductScreen(productId, selectedProducts)) {
    return <Navigate to="/workflow/product-selection" replace />;
  }

  return <Suspense fallback={<LoadingScreen logo={logo} />}>{children}</Suspense>;
};

/**
 * Router Component
 *
 * Lazy-loaded routing configuration for Frisia Sluitstraat.
 * Product screens (woonhuis, inboedel, aansprakelijkheid) are gated
 * behind product selection to prevent direct URL access.
 */
const Router: React.FC = () => {
  const { config } = useConfig();
  const logo = config?.customer?.logo;

  return (
    <Suspense fallback={<LoadingScreen logo={logo} />}>
      <Routes>
        {/* Root route - Redirect to workflow */}
        <Route path="/" element={<Navigate to="/workflow/product-selection" replace />} />

        {/* Settings routes */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/customer" element={<CustomerInformationPage />} />
        <Route path="/settings/module" element={<ModuleSelectionPage />} />
        <Route path="/settings/sluitstraat" element={<SluitstraatInformationPage />} />
        <Route path="/settings/workflow" element={<WorkflowConfigurationPage />} />
        <Route path="/settings/content" element={<TextsPage />} />

        {/* Generated workflow screens */}
        <Route path="/workflow" element={<Navigate to="/workflow/product-selection" replace />} />
        <Route path="/workflow/product-selection" element={<ProductSelectionScreen />} />

        <Route path="/workflow/client-information" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><ClientInformationScreen /></Suspense>
        } />

        {/* Product screens - gated behind product selection */}
        <Route path="/workflow/woonhuis" element={
          <ProductRoute productId="woonhuis" logo={logo}><WoonhuisCvbScreen /></ProductRoute>
        } />
        <Route path="/workflow/inboedel" element={
          <ProductRoute productId="inboedel" logo={logo}><InboedelCvbScreen /></ProductRoute>
        } />
        <Route path="/workflow/aansprakelijkheid" element={
          <ProductRoute productId="aansprakelijkheid" logo={logo}><AansprakelijkheidCvbScreen /></ProductRoute>
        } />

        {/* Quote flow - Final screen */}
        <Route path="/workflow/summary" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><WorkflowSummaryScreen /></Suspense>
        } />

        {/* Application flow screens */}
        <Route path="/workflow/payment-information" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><PaymentInformationScreen /></Suspense>
        } />
        <Route path="/workflow/acceptance-questions" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><AcceptanceQuestionsScreen /></Suspense>
        } />
        <Route path="/workflow/confirmation" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><ConfirmationScreen /></Suspense>
        } />
        <Route path="/workflow/end" element={
          <Suspense fallback={<LoadingScreen logo={logo} />}><EndPageScreen /></Suspense>
        } />

        {/* Error routes */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
